'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { Calendar, Clock, Search, Loader2 } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';

interface BlogPost {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  author: string;
  publishedAt: string;
  readTime: number;
  featured: boolean;
}

const CATEGORIES = ['Tutti', 'Guide', 'Compliance', 'Tecnico', 'Analytics', 'Prodotto'];

export default function BlogPage() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('Tutti');

  useEffect(() => {
    fetch('/api/help/kb')
      .then(r => r.json())
      .then(data => {
        const articles = data.data ?? data.articles ?? [];
        const mapped: BlogPost[] = articles.map((a: any) => ({
          id: a.id,
          slug: a.slug ?? a.id,
          title: a.title ?? '',
          excerpt: a.excerpt ?? a.content?.substring(0, 150) ?? '',
          category: a.category ?? 'Guide',
          author: a.author ?? 'Redazione',
          publishedAt: a.published_at ?? a.created_at ?? new Date().toISOString(),
          readTime: a.read_time ?? 5,
          featured: a.featured ?? false,
        }));
        setPosts(mapped);
      })
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = posts.filter(p => {
    const matchSearch = !search || p.title.toLowerCase().includes(search.toLowerCase()) || p.excerpt.toLowerCase().includes(search.toLowerCase());
    const matchCat = category === 'Tutti' || p.category === category;
    return matchSearch && matchCat;
  });

  const featured = filtered.find(p => p.featured);
  const rest = filtered.filter(p => !p.featured);

  return (
    <div className="min-h-screen bg-white">
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-border">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link href="/" className="flex items-center gap-2">
            <AppLogo size={28} />
            <span className="font-semibold text-sm text-foreground">LabCall<span className="text-primary">Manager</span></span>
          </Link>
          <Link href="/register/lab" className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90">Inizia Gratis</Link>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-10">
          <h1 className="text-4xl font-bold text-foreground mb-3">Blog</h1>
          <p className="text-muted-foreground">Guide, best practice e novità per i laboratori di analisi.</p>
        </div>

        {/* Search & Filter */}
        <div className="flex flex-col sm:flex-row gap-4 mb-10">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Cerca articoli..." className="w-full pl-9 pr-4 py-2.5 rounded-lg border border-border text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
          </div>
          <div className="flex gap-2 flex-wrap">
            {CATEGORIES.map(c => (
              <button key={c} onClick={() => setCategory(c)} className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${category === c ? 'bg-primary text-white' : 'bg-muted text-muted-foreground hover:text-foreground'}`}>{c}</button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 size={24} className="animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            {/* Featured */}
            {featured && (
              <Link href={`/blog/${featured.slug}`} className="block mb-10 group">
                <div className="rounded-2xl border border-border overflow-hidden hover:shadow-lg transition-shadow">
                  <div className="bg-gradient-to-br from-primary/10 to-blue-50 h-48 flex items-center justify-center">
                    <span className="text-6xl">📋</span>
                  </div>
                  <div className="p-8">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="px-2.5 py-1 bg-primary/10 text-primary text-xs font-medium rounded-full">{featured.category}</span>
                      <span className="text-xs text-muted-foreground">In evidenza</span>
                    </div>
                    <h2 className="text-2xl font-bold text-foreground mb-3 group-hover:text-primary transition-colors">{featured.title}</h2>
                    <p className="text-muted-foreground mb-4">{featured.excerpt}</p>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1"><Calendar size={12} />{new Date(featured.publishedAt).toLocaleDateString('it-IT')}</span>
                      <span className="flex items-center gap-1"><Clock size={12} />{featured.readTime} min</span>
                      <span>{featured.author}</span>
                    </div>
                  </div>
                </div>
              </Link>
            )}

            {/* Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {rest.map(post => (
                <Link key={post.id} href={`/blog/${post.slug}`} className="group block rounded-2xl border border-border overflow-hidden hover:shadow-md transition-shadow">
                  <div className="bg-gradient-to-br from-muted to-muted/50 h-36 flex items-center justify-center">
                    <span className="text-4xl">{post.category === 'Guide' ? '📖' : post.category === 'Compliance' ? '⚖️' : post.category === 'Tecnico' ? '⚙️' : post.category === 'Analytics' ? '📊' : '🚀'}</span>
                  </div>
                  <div className="p-5">
                    <span className="px-2 py-0.5 bg-muted text-muted-foreground text-xs font-medium rounded-full">{post.category}</span>
                    <h3 className="font-semibold text-foreground mt-2 mb-2 group-hover:text-primary transition-colors line-clamp-2">{post.title}</h3>
                    <p className="text-xs text-muted-foreground line-clamp-2 mb-3">{post.excerpt}</p>
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>{new Date(post.publishedAt).toLocaleDateString('it-IT')}</span>
                      <span className="flex items-center gap-1"><Clock size={11} />{post.readTime} min</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {filtered.length === 0 && (
              <div className="text-center py-16 text-muted-foreground">
                <p className="text-lg font-medium mb-2">Nessun articolo trovato</p>
                <p className="text-sm">Prova a modificare i filtri di ricerca.</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
